import { useEffect, useMemo, useRef, useState } from "react";
import { OutputPreview } from "../components/OutputPreview.js";
import { StatusChip } from "../components/StatusChip.js";
import { api, ApiError } from "../lib/api.js";
import { hashForRoute } from "../lib/hashRoute.js";
import { nextRating } from "../lib/ratingState.js";
import { NON_TERMINAL_JOB_STATUSES } from "../lib/types.js";
import type {
  ContentType,
  Deliverable,
  Item,
  Job,
  OutputWithUrl,
  Project,
  Rating,
} from "../lib/types.js";
import { usePolling } from "../lib/usePolling.js";

const CONTENT_TYPES: ContentType[] = ["text", "image", "svg", "audio", "video"];

const selectCls =
  "rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

const btnGhost =
  "rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 " +
  "disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800";

const btnPrimary =
  "rounded-md bg-(--accent) px-3 py-1.5 text-sm font-medium text-(--accent-contrast) " +
  "hover:bg-(--accent-hover) disabled:opacity-50";

interface DeliverableDetail {
  deliverable: Deliverable;
  jobs: Job[];
  outputs: OutputWithUrl[];
}

export function ReviewQueue() {
  const [all, setAll] = useState<Deliverable[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [projectFilter, setProjectFilter] = useState("");
  const [ctFilter, setCtFilter] = useState("");
  const [index, setIndex] = useState(0);

  const [detail, setDetail] = useState<DeliverableDetail | null>(null);
  const [itemTitle, setItemTitle] = useState("");
  const [pickedOutputId, setPickedOutputId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const loadingMore = useRef(false);

  useEffect(() => {
    void (async () => {
      try {
        const [rev, projs] = await Promise.all([
          api<{ items: Deliverable[]; nextCursor?: string }>("/api/review"),
          api<{ items: Project[] }>("/api/projects"),
        ]);
        setAll(rev.items);
        setCursor(rev.nextCursor);
        setProjects(projs.items);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : "Could not load the review queue.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const visible = useMemo(
    () =>
      all.filter(
        (d) =>
          (!projectFilter || d.projectId === projectFilter) &&
          (!ctFilter || d.contentType === ctFilter),
      ),
    [all, projectFilter, ctFilter],
  );

  useEffect(() => {
    setIndex((i) => Math.max(0, Math.min(i, visible.length - 1)));
  }, [visible.length]);

  const current = visible[index] ?? null;

  async function loadMore() {
    if (loadingMore.current || !cursor) return;
    loadingMore.current = true;
    try {
      const res = await api<{ items: Deliverable[]; nextCursor?: string }>(
        `/api/review?cursor=${encodeURIComponent(cursor)}`,
      );
      setAll((prev) => [...prev, ...res.items]);
      setCursor(res.nextCursor);
    } catch {
      /* a failed page just means no more items for now */
    } finally {
      loadingMore.current = false;
    }
  }

  useEffect(() => {
    if (cursor && visible.length - index <= 1) void loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, visible.length, cursor]);

  useEffect(() => {
    setDetail(null);
    setPickedOutputId(null);
    if (!current) return;
    let cancelled = false;
    void (async () => {
      try {
        const d = await api<DeliverableDetail>(`/api/deliverables/${current.deliverableId}`);
        if (cancelled) return;
        setDetail(d);
        const it = await api<Item>(`/api/projects/${current.projectId}/items/${current.itemId}`);
        if (!cancelled) setItemTitle(it.title);
      } catch {
        /* leave the card in its loading state; the queue still works */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.deliverableId, current?.projectId, current?.itemId]);

  const outputs = detail?.outputs ?? [];
  const currentOutput =
    outputs.find((o) => o.outputId === pickedOutputId) ??
    outputs.find((o) => o.chosen) ??
    outputs[0] ??
    null;

  function forget(deliverableId: string) {
    setAll((prev) => prev.filter((d) => d.deliverableId !== deliverableId));
  }

  async function reloadDetail() {
    if (!current) return;
    try {
      setDetail(await api<DeliverableDetail>(`/api/deliverables/${current.deliverableId}`));
    } catch {
      /* ignore */
    }
  }

  // A regenerate triggered with `r` leaves a job queued; poll the open card so
  // it updates in place instead of appearing to hang.
  const detailJobInFlight = useMemo(
    () =>
      (detail?.jobs ?? []).some((j) =>
        (NON_TERMINAL_JOB_STATUSES as readonly string[]).includes(j.status),
      ),
    [detail],
  );
  usePolling(() => reloadDetail(), { enabled: detailJobInFlight });

  async function act<T>(fn: () => Promise<T>) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "That action failed.");
    } finally {
      setBusy(false);
    }
  }

  function rate(clicked: Rating) {
    if (!currentOutput) return;
    const resolved = nextRating(currentOutput.rating ?? null, clicked);
    void act(async () => {
      await api(`/api/outputs/${currentOutput.outputId}/rate`, {
        method: "POST",
        json: { rating: resolved },
      });
      setFlash(resolved ? `Rated ${resolved}` : "Rating cleared");
      await reloadDetail();
    });
  }

  function accept() {
    if (!current || !currentOutput) return;
    void act(async () => {
      await api(`/api/deliverables/${current.deliverableId}/accept`, {
        method: "POST",
        json: { outputId: currentOutput.outputId },
      });
      setFlash("Accepted");
      forget(current.deliverableId);
    });
  }

  function reject() {
    if (!current) return;
    void act(async () => {
      await api(`/api/deliverables/${current.deliverableId}/reject`, { method: "POST" });
      setFlash("Rejected");
      forget(current.deliverableId);
    });
  }

  function regenerate() {
    if (!current) return;
    void act(async () => {
      await api(`/api/deliverables/${current.deliverableId}/generate`, {
        method: "POST",
        json: { feedback: true },
      });
      setFlash("Regenerating");
      forget(current.deliverableId);
    });
  }

  // Keyboard flow (j/k move, u/d rate, a accept, r regenerate). Re-bound every
  // render on purpose so the handlers always see the latest current output.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)
      ) {
        return;
      }
      switch (e.key) {
        case "j":
          e.preventDefault();
          setIndex((i) => Math.min(i + 1, visible.length - 1));
          break;
        case "k":
          e.preventDefault();
          setIndex((i) => Math.max(0, i - 1));
          break;
        case "u":
          e.preventDefault();
          rate("up");
          break;
        case "d":
          e.preventDefault();
          rate("down");
          break;
        case "a":
          e.preventDefault();
          accept();
          break;
        case "r":
          e.preventDefault();
          regenerate();
          break;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (loading) {
    return <p className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading...</p>;
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Review</h1>
        <div className="flex items-center gap-2">
          <select
            className={selectCls}
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <option value="">All projects</option>
            {projects.map((p) => (
              <option key={p.projectId} value={p.projectId}>
                {p.name}
              </option>
            ))}
          </select>
          <select className={selectCls} value={ctFilter} onChange={(e) => setCtFilter(e.target.value)}>
            <option value="">All types</option>
            {CONTENT_TYPES.map((ct) => (
              <option key={ct} value={ct}>
                {ct}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="mt-1 text-xs text-gray-400">
        Keys: <kbd>j</kbd>/<kbd>k</kbd> move, <kbd>u</kbd>/<kbd>d</kbd> rate, <kbd>a</kbd> accept,{" "}
        <kbd>r</kbd> regenerate
      </p>

      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {visible.length === 0 ? (
        <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
          Nothing waiting for review.
        </p>
      ) : !current ? (
        <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
          Reached the end of the queue.
        </p>
      ) : (
        <div className="mt-4 rounded-md border border-gray-200 p-4 dark:border-gray-800">
          <div className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>
              {index + 1} of {visible.length}
              {cursor ? "+" : ""}
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                className={btnGhost}
                disabled={index === 0}
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
              >
                Prev
              </button>
              <button
                type="button"
                className={btnGhost}
                disabled={index >= visible.length - 1}
                onClick={() => setIndex((i) => Math.min(i + 1, visible.length - 1))}
              >
                Next
              </button>
            </div>
          </div>

          <div className="mt-2">
            <a
              href={hashForRoute({
                kind: "itemDetail",
                projectId: current.projectId,
                itemId: current.itemId,
              })}
              className="font-medium text-gray-900 hover:underline dark:text-gray-100"
            >
              {itemTitle || current.itemId}
            </a>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {current.key} - {current.contentType}{" "}
              <StatusChip status={current.status} label={current.status} />
            </div>
          </div>

          {!detail ? (
            <p className="mt-4 text-xs text-gray-400">Loading output...</p>
          ) : currentOutput ? (
            <div className="mt-3">
              <OutputPreview output={currentOutput} />
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Current rating: {currentOutput.rating ?? "unrated"}
                {currentOutput.chosen ? " - chosen" : ""}
              </div>
              {outputs.length > 1 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {outputs.map((o, i) => (
                    <button
                      key={o.outputId}
                      type="button"
                      onClick={() => setPickedOutputId(o.outputId)}
                      className={`rounded border px-2 py-0.5 text-xs ${
                        o.outputId === currentOutput.outputId
                          ? "border-(--accent) text-(--accent)"
                          : "border-gray-300 text-gray-500 dark:border-gray-700 dark:text-gray-400"
                      }`}
                    >
                      #{i + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="mt-4 text-xs text-gray-400">This deliverable has no outputs yet.</p>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className={btnGhost} disabled={busy || !currentOutput} onClick={() => rate("up")}>
              &#9650; Up
            </button>
            <button type="button" className={btnGhost} disabled={busy || !currentOutput} onClick={() => rate("down")}>
              &#9660; Down
            </button>
            <button type="button" className={btnPrimary} disabled={busy || !currentOutput} onClick={accept}>
              Accept
            </button>
            <button type="button" className={btnGhost} disabled={busy} onClick={reject}>
              Reject
            </button>
            <button type="button" className={btnGhost} disabled={busy} onClick={regenerate}>
              Regenerate
            </button>
          </div>

          {flash && <p className="mt-2 text-xs text-gray-400">{flash}</p>}
        </div>
      )}
    </div>
  );
}
